/**
 * Start: a person's commitment. The stages the request still has to walk
 * are walked as mechanics and it starts; Backoffice dispatch follows where
 * partner work was approved. What Start still needs is said first, and a
 * refusal (a gate the API holds) says what it needs.
 */

import { useState } from 'react';

import { Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlayerPlay } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import { delivered, type PrefillDelivery } from '@/types/workbench';
import { toError } from '@/utils/errors';

/** What became of the partner's form at Start, in a line: each dispatched
 * assignment's copy delivered to its request, or why not. Null when none
 * was due. */
export function deliveriesLine(deliveries: PrefillDelivery[]): string | null {
  if (deliveries.length === 0) {
    return null;
  }
  const done = deliveries.filter(delivered).length;
  const parts = done > 0 ? [`${done} ${done === 1 ? 'copy' : 'copies'} delivered`] : [];
  for (const delivery of deliveries) {
    const why = delivery.detail ? ` — ${delivery.detail}` : '';
    if (delivery.outcome === 'refused') {
      parts.push(`one refused${why}`);
    } else if (delivery.outcome === 'failed') {
      parts.push(`one not delivered${why}; Deliver it from Partner work`);
    } else if (delivery.outcome === 'not_cut') {
      parts.push(`one not cut${why}`);
    }
  }
  return `The partner's form: ${parts.join('; ')}.`;
}

interface StartButtonProps {
  reportId: string;
  /** What Start still needs, in words; empty when it can start. */
  needs: string[];
  canStart: boolean;
  /** After Start: the case refetches. */
  onStarted: () => void | Promise<void>;
}

export function StartButton({ reportId, needs, canStart, onStarted }: Readonly<StartButtonProps>) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const start = async () => {
    setBusy(true);
    setRefused(null);
    try {
      const started = await vnextApiClient.startSetupCase(reportId);
      const forms = deliveriesLine(started.deliveries);
      notifications.show({
        color: started.deliveries.every(delivered) ? 'teal' : 'orange',
        message: [`Started — the request is ${started.stage.replace(/_/g, ' ')}.`, forms]
          .filter(Boolean)
          .join(' '),
      });
      setOpen(false);
      await onStarted();
    } catch (err) {
      if (err instanceof ApiConflictError) {
        setRefused(err.message);
      } else {
        Sentry.captureException(toError(err));
        setRefused(toError(err, 'Not started').message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="xs"
        leftSection={<IconPlayerPlay size={14} />}
        onClick={() => setOpen(true)}
        variant={canStart ? 'filled' : 'default'}
        data-testid="start-case"
      >
        Start
      </Button>
      <Modal opened={open} onClose={() => setOpen(false)} title="Start this case?" centered>
        <Stack gap="sm">
          {needs.length > 0 ? (
            <>
              <Text size="sm">Start still needs:</Text>
              <List size="sm" data-testid="start-needs">
                {needs.map((need) => (
                  <List.Item key={need}>{need}</List.Item>
                ))}
              </List>
            </>
          ) : (
            <Text size="sm">
              The request starts: the stages it hasn&apos;t walked are walked, partner work approved
              is dispatched, and the case agent carries on under the case budget.
            </Text>
          )}
          {refused ? (
            <Text size="sm" c="red" data-testid="start-refused">
              {refused}
            </Text>
          ) : null}
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              loading={busy}
              onClick={() => void start()}
              data-testid="start-case-confirm"
            >
              Start
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
